import type { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import type { TypedUseQueryStateResult } from '@reduxjs/toolkit/dist/query/react';
import { get } from 'lodash-es';
import { useEffect } from 'react';

import { useStoreDispatch } from '@hook/store/store.hooks';
import { BaseStoreInterface } from '@interface/store/base-store.interface';
import { UNLOADED_STATUS } from '@util/constants/api.constants';
import { isExist } from '@util/data/is-exist';

interface ReducerActionsInterface {
  startRequest: ActionCreatorWithoutPayload;
  setLoaded: ActionCreatorWithPayload<boolean>;
  endRequest: ActionCreatorWithPayload<Pick<BaseStoreInterface, 'error'>>;
}

export const useQuery = <
  RT = any,
  D extends TypedUseQueryStateResult<RT, any, any> = TypedUseQueryStateResult<RT, any, any>,
>(
  call: D,
  actions: ReducerActionsInterface,
  setData: ActionCreatorWithPayload<RT>,
) => {
  const dispatch = useStoreDispatch();

  const { data, error, status } = call;

  useEffect(
    () =>
      void dispatch(
        UNLOADED_STATUS.has(status) ? actions.startRequest() : actions.endRequest({ error: get(error, 'data', null) }),
      ),
    [status, error],
  );

  useEffect(() => void (isExist(data) && dispatch(setData(data as RT))), [data, setData]);

  useEffect(() => void dispatch(actions.setLoaded(!UNLOADED_STATUS.has(status))), [status]);

  return call;
};
